import { useLayoutEffect, useRef, useState } from "react";
import { reserveEmphasisWidth } from "@/lib/reserve-emphasis-width";

// Tab bar for sub-navigation within a pillar (Save / Grow / Share / Match).
// Horizontal items with an accent underline on the active tab. Generic
// over the items so the consumer composes labels + routes; the primitive
// itself owns only the visual pattern.
//
// Der Unterstrich ist EIN gleitendes Element (.smycles-tab-indicator), nicht
// eine Linie pro Reiter: so wandert er beim Wechsel zur neuen Beschriftung,
// statt dort hart zu erscheinen. Die Mechanik ist dieselbe wie beim
// SegmentedControl - Lage und Breite werden aus der Box des aktiven Reiters
// gemessen und als transform + width gesetzt. Vor der ersten Messung gibt es
// ihn nicht, deshalb steht er beim Erscheinen sofort richtig und faehrt nie
// von links ein.

interface TabItem {
  id: string;
  label: string;
}

interface PillarTabsProps {
  items: readonly TabItem[];
  activeId: string;
  onSelect: (id: string) => void;
  /**
   * Seitenbreite Leiste mit eigener Einrückung (Default) oder eingebettet in einen
   * Container, der seine Polsterung schon mitbringt - dann ohne px/py. Additiv,
   * bestehende Aufrufer bleiben gleich.
   */
  padded?: boolean;
  className?: string;
  /** Testanker: `${idPrefix}-<item.id>` am Reiter. */
  idPrefix?: string;
}

interface IndicatorRect {
  x: number;
  w: number;
}

export function PillarTabs({ items, activeId, onSelect, padded = true, className = "", idPrefix }: PillarTabsProps) {
  const navRef = useRef<HTMLElement>(null);
  const tabsRef = useRef(new Map<string, HTMLButtonElement>());
  const [indicator, setIndicator] = useState<IndicatorRect | null>(null);

  // Stabiler Schlüssel über die Reiter: kommt einer dazu oder fällt einer weg,
  // verschiebt sich der aktive. `items` selbst taugt nicht als Abhängigkeit,
  // Aufrufer reichen bei jedem Render ein frisches Array herein.
  const itemsKey = items.map((t) => t.id).join("|");

  useLayoutEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    const measure = () => {
      for (const tab of tabsRef.current.values()) {
        const label = tab.querySelector<HTMLElement>(".smycles-emphasis-label");
        if (label) reserveEmphasisWidth(label);
      }
      const active = tabsRef.current.get(activeId);
      // Kein passender Reiter: Strich verbergen, statt ihn auf dem alten stehen zu lassen.
      if (!active) {
        setIndicator(null);
        return;
      }
      const next = { x: active.offsetLeft, w: active.offsetWidth };
      // Gleiche Werte nicht als neues Objekt setzen, sonst rendert jeder
      // Eltern-Render, der diesen Effekt erneut anstößt, ein zweites Mal.
      setIndicator((prev) => (prev && prev.x === next.x && prev.w === next.w ? prev : next));
    };
    measure();
    // Nachträgliche Verschiebungen: Beschriftungsbreite hängt an der Sprache,
    // die Umbruchlage an der Breite des Containers.
    const observer = new ResizeObserver(measure);
    observer.observe(nav);
    return () => observer.disconnect();
  }, [activeId, itemsKey]);

  if (items.length === 0) return null;
  return (
    <nav
      ref={navRef}
      className={`relative flex items-center gap-8 border-b border-ink/10 text-smycles-meta ${
        padded ? "px-10 py-4" : "pb-3"
      } ${className}`}
    >
      {indicator ? (
        <span
          aria-hidden
          className="smycles-tab-indicator"
          style={{ transform: `translateX(${indicator.x}px)`, width: indicator.w }}
        />
      ) : null}
      {items.map((t) => {
        const active = t.id === activeId;
        return (
          <button
            key={t.id}
            ref={(el) => {
              if (el) tabsRef.current.set(t.id, el);
              else tabsRef.current.delete(t.id);
            }}
            type="button"
            id={idPrefix && `${idPrefix}-${t.id}`}
            // Der Strich liegt jetzt in der Leiste, nicht im Reiter: ohne diese
            // Angabe sagt das Markup nicht mehr, welcher Reiter der aktuelle ist.
            aria-current={active ? "true" : undefined}
            onClick={() => onSelect(t.id)}
            className={`relative pb-1 transition ${active ? "font-semibold text-ink" : "font-normal text-ink-muted hover:text-ink"}`}
          >
            <span className="smycles-emphasis-label">{t.label}</span>
          </button>
        );
      })}
    </nav>
  );
}
